import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { fmtMDT } from "../lib/time";
import { cn } from "../lib/utils";
import IncidentDetailModal from "../components/IncidentDetailModal";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell,
} from "recharts";
import {
  ArrowLeft, ExternalLink, Flame, RefreshCw, Users, Clock, AlertTriangle,
  CheckCircle2, FileText, Calendar,
} from "lucide-react";

// Severity → color (tune to your brand as needed).
const SEV_COLOR = {
  "E1 - Critical": { bg: "bg-rose-500/10", text: "text-rose-700 dark:text-rose-300", border: "border-rose-500/40", hex: "#ef4444" },
  "E2 - Major":    { bg: "bg-amber-500/10", text: "text-amber-700 dark:text-amber-300", border: "border-amber-500/40", hex: "#f59e0b" },
  "E3 - Low":      { bg: "bg-sky-500/10", text: "text-sky-700 dark:text-sky-300", border: "border-sky-500/40", hex: "#0ea5e9" },
};
const DEFAULT_SEV = { bg: "bg-muted", text: "text-muted-foreground", border: "border-border", hex: "#94a3b8" };
const sevStyle = (name) => SEV_COLOR[name] || DEFAULT_SEV;

// Status category → pill class
const STATUS_STYLE = {
  triage:     "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/40",
  live:       "bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/40",
  learning:   "bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/40",
  closed:     "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/40",
  paused:     "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/40",
  canceled:   "bg-muted text-muted-foreground border-border",
  declined:   "bg-muted text-muted-foreground border-border",
};

function humanSeconds(s) {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return remM ? `${h}h ${remM}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

function KpiCard({ label, value, sub, icon: Icon, tone = "default" }) {
  const toneCls = {
    default: "border-border bg-card",
    warn:    "border-amber-500/40 bg-amber-500/5",
    danger:  "border-rose-500/40 bg-rose-500/5",
    good:    "border-emerald-500/40 bg-emerald-500/5",
  }[tone];
  return (
    <div className={cn("rounded-xl border p-4", toneCls)}>
      <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="mt-1 text-[26px] font-semibold tracking-tight">{value}</div>
      {sub && <div className="text-[11.5px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function SeverityPill({ name }) {
  const s = sevStyle(name);
  return (
    <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5", s.bg, s.text, s.border)}>
      {name || "—"}
    </span>
  );
}

function StatusPill({ name, category }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", STATUS_STYLE[category] || "bg-muted text-muted-foreground border-border")}>
      {name || category || "—"}
    </span>
  );
}

export default function IncidentIO() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!data) setLoading(true); else setRefreshing(true);
    try {
      const { data: payload } = await api.get(`/incident-io/dashboard${force ? "?force=1" : ""}`);
      setData(payload);
    } catch (e) {
      toast({ title: "Failed to load incident.io dashboard", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); setRefreshing(false); }
  }, [data, toast]);

  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const chartData = useMemo(() => (data?.mttr_trend || []).map(w => ({ ...w, hours: +(w.mttr_minutes / 60).toFixed(1) })), [data]);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-24 rounded-xl bg-muted/40 animate-pulse" />
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-muted/40 animate-pulse" />)}
        </div>
        <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />
      </div>
    );
  }

  if (!data?.live) {
    return (
      <div className="space-y-4" data-testid="incident-io-page">
        <Header navigate={navigate} onRefresh={() => load(true)} refreshing={refreshing} generatedAt={null} />
        <div className="rounded-2xl border border-destructive/40 bg-destructive/10 text-destructive p-4">
          Unable to reach incident.io. {data?.error ? <span className="text-[12px] opacity-80">{data.error}</span> : null}
        </div>
      </div>
    );
  }

  const k = data.kpis || {};
  const mttrChartColor = "#f97316"; // orange

  return (
    <div className="space-y-5" data-testid="incident-io-page">
      <Header navigate={navigate} onRefresh={() => load(true)} refreshing={refreshing} generatedAt={data.generated_at} />

      {/* KPI STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="incident-io-kpis">
        <KpiCard
          label="Open incidents"
          value={k.open_count ?? 0}
          sub={k.open_count ? "Live / triage / learning" : "All quiet"}
          icon={AlertTriangle}
          tone={k.open_count > 0 ? "warn" : "good"}
        />
        <KpiCard
          label="Resolved (30d)"
          value={k.closed_30d ?? 0}
          sub={`of ${k.total_incidents ?? 0} lifetime`}
          icon={CheckCircle2}
          tone="default"
        />
        <KpiCard
          label="MTTR (30d)"
          value={k.mttr_30d_human || "—"}
          sub="Mean time to resolve"
          icon={Clock}
          tone="default"
        />
        <KpiCard
          label="On-call now"
          value={k.oncall_count ?? 0}
          sub={`${k.schedules_count ?? 0} schedule${(k.schedules_count ?? 0) === 1 ? "" : "s"}`}
          icon={Users}
          tone="default"
        />
      </div>

      {/* CHARTS ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-xl border border-border bg-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13.5px] font-semibold">MTTR trend — last 12 weeks</h3>
            <span className="text-[11px] text-muted-foreground">Hours to resolve, per week</span>
          </div>
          {chartData.length === 0 || chartData.every(d => !d.hours) ? (
            <div className="h-56 flex items-center justify-center text-[13px] text-muted-foreground">No resolved incidents in the last 12 weeks.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => `${v}h`} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                  formatter={(v, _n, obj) => [`${v}h`, `${obj.payload.count} incident${obj.payload.count === 1 ? "" : "s"}`]}
                />
                <Line type="monotone" dataKey="hours" stroke={mttrChartColor} strokeWidth={2} dot={{ r: 3, fill: mttrChartColor }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13.5px] font-semibold">Open by severity</h3>
            <span className="text-[11px] text-muted-foreground">Currently active</span>
          </div>
          {(data.severity_breakdown || []).length === 0 ? (
            <div className="h-56 flex items-center justify-center text-[13px] text-muted-foreground">Nothing open right now.</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.severity_breakdown} layout="vertical" margin={{ top: 4, right: 12, left: 12, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis type="category" dataKey="severity" stroke="hsl(var(--muted-foreground))" fontSize={11} width={80} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.severity_breakdown.map((s, i) => (
                    <Cell key={i} fill={sevStyle(s.severity).hex} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ON-CALL */}
      <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="incident-io-oncall">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center"><Calendar className="w-4 h-4 text-primary" /></div>
            <h3 className="text-[14px] font-semibold">On-call schedule</h3>
            <span className="text-[11px] text-muted-foreground">{data.oncall.length} active shift{data.oncall.length === 1 ? "" : "s"}</span>
          </div>
        </div>
        {data.oncall.length === 0 ? (
          <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">Nobody is currently on-call.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.oncall.map((o, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-center px-4 py-3">
                <div className="col-span-4">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{o.schedule_name}</div>
                  <div className="font-semibold text-[13.5px] mt-0.5">{o.user_name || "Unassigned"}</div>
                  <div className="text-[11.5px] text-muted-foreground">{o.user_email || "—"}</div>
                </div>
                <div className="col-span-3 text-[12px]">
                  <div className="text-muted-foreground">Started</div>
                  <div className="font-medium">{fmtMDT(o.start_at)}</div>
                </div>
                <div className="col-span-3 text-[12px]">
                  <div className="text-muted-foreground">Until</div>
                  <div className="font-medium">{fmtMDT(o.end_at)}</div>
                </div>
                <div className="col-span-2 text-right">
                  {o.permalink && (
                    <a href={o.permalink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary text-[12px] hover:underline">
                      Schedule <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* OPEN INCIDENTS */}
      <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="incident-io-open-table">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center"><Flame className="w-4 h-4 text-rose-500" /></div>
            <h3 className="text-[14px] font-semibold">Open incidents</h3>
            <span className="text-[11px] text-muted-foreground">{data.open_incidents.length}</span>
          </div>
        </div>
        {data.open_incidents.length === 0 ? (
          <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">🎉 No open incidents right now.</div>
        ) : (
          <IncidentTable rows={data.open_incidents} onRowClick={setSelectedId} />
        )}
      </section>

      {/* RECENT RESOLVED + POST-MORTEMS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="rounded-xl border border-border bg-card overflow-hidden lg:col-span-2" data-testid="incident-io-resolved">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-500" /></div>
              <h3 className="text-[14px] font-semibold">Recent resolved</h3>
              <span className="text-[11px] text-muted-foreground">Last {data.recent_resolved.length}</span>
            </div>
          </div>
          {data.recent_resolved.length === 0 ? (
            <div className="px-4 py-10 text-center text-[13px] text-muted-foreground">No resolved incidents yet.</div>
          ) : (
            <IncidentTable rows={data.recent_resolved} showResolved onRowClick={setSelectedId} />
          )}
        </section>

        <section className="rounded-xl border border-border bg-card overflow-hidden" data-testid="incident-io-postmortems">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center"><FileText className="w-4 h-4 text-violet-500" /></div>
              <h3 className="text-[14px] font-semibold">Post-mortems</h3>
              <span className="text-[11px] text-muted-foreground">{data.post_mortems.length}</span>
            </div>
          </div>
          {data.post_mortems.length === 0 ? (
            <div className="px-4 py-8 text-center text-[12.5px] text-muted-foreground">
              No published post-mortems yet.<br />
              Debriefs will appear here once flagged.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {data.post_mortems.map((pm) => (
                <button key={pm.id} type="button" onClick={() => setSelectedId(pm.id)} className="block w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors">
                  <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground">{pm.reference}</div>
                  <div className="font-semibold text-[13px] mt-0.5 line-clamp-2">{pm.name}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <SeverityPill name={pm.severity_name} />
                    <span className="text-[11px] text-muted-foreground">{humanSeconds(pm.duration_seconds)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <IncidentDetailModal
        incidentId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        onEdited={() => load(true)}
      />
    </div>
  );
}

function Header({ navigate, onRefresh, refreshing, generatedAt }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1.5">
          <span className="cursor-pointer hover:text-foreground" onClick={() => navigate("/")}>Dashboard</span>
          <span>›</span>
          <span className="cursor-pointer hover:text-foreground" onClick={() => navigate("/apps")}>Apps</span>
          <span>›</span>
          <span className="inline-flex items-center gap-1 text-foreground font-medium">
            <Flame className="w-3.5 h-3.5 text-orange-500" /> incident.io
          </span>
        </div>
        <h1 className="text-[26px] font-semibold tracking-tight mt-2">incident.io</h1>
        <p className="text-[13px] text-muted-foreground">
          Live response, on-call rotations and post-mortem library.
          {generatedAt && <> · Refreshed {fmtMDT(generatedAt)}</>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-1.5" data-testid="incident-io-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <Button variant="outline" size="sm" onClick={onRefresh} className="gap-1.5" data-testid="incident-io-refresh-btn">
          <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} /> Refresh
        </Button>
        <Button asChild size="sm" className="gap-1.5" data-testid="incident-io-open-external-btn">
          <a href="https://app.incident.io" target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4" /> Open in incident.io
          </a>
        </Button>
      </div>
    </div>
  );
}

function IncidentTable({ rows, showResolved = false, onRowClick }) {
  return (
    <div>
      <div className="grid grid-cols-12 gap-3 px-4 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
        <div className="col-span-1">Ref</div>
        <div className="col-span-4">Name</div>
        <div className="col-span-2">Severity</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2">{showResolved ? "Duration" : "Lead"}</div>
        <div className="col-span-1 text-right">Link</div>
      </div>
      <div className="divide-y divide-border">
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`incident-row-${r.reference}`}
            onClick={() => onRowClick?.(r.id)}
            className="grid grid-cols-12 gap-3 items-center px-4 py-2.5 text-[12.5px] hover:bg-muted/40 cursor-pointer transition-colors"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick?.(r.id); } }}
          >
            <div className="col-span-1 font-mono text-[11px] text-muted-foreground">{r.reference}</div>
            <div className="col-span-4 min-w-0">
              <div className="font-medium truncate" title={r.name}>{r.name}</div>
              {r.incident_type && r.incident_type !== "Undefined" && (
                <div className="text-[10.5px] text-muted-foreground truncate">{r.incident_type}</div>
              )}
            </div>
            <div className="col-span-2"><SeverityPill name={r.severity_name} /></div>
            <div className="col-span-2"><StatusPill name={r.status_name} category={r.status_category} /></div>
            <div className="col-span-2 text-[12px] text-muted-foreground">
              {showResolved ? humanSeconds(r.duration_seconds) : (r.incident_lead || "—")}
            </div>
            <div className="col-span-1 text-right">
              {r.permalink && (
                <a href={r.permalink} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center text-primary hover:underline" title="Open in incident.io">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
